import React, { useMemo } from "react";
import { ServiceCreditType } from "@/app/graphql/codegen";
import { icon } from "@/app/style/icons";
import { Box, Card, Chip, Grid2, IconButton, Stack, Typography, useTheme, Skeleton, Alert } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { RouteTo } from "@routing/RouteMap";

interface ServiceCredit {
    id: string;
    type: ServiceCreditType;
    remainingUses: number;
    expiresAt?: string | null;
}

interface ServiceCreditsDisplayProps {
    serviceCredits: ServiceCredit[];
    isLoading?: boolean;
}

// Mapping of service credit types to human-readable names
const serviceCreditsLabels: Record<ServiceCreditType, string> = {
    [ServiceCreditType.QuickWesentlichkeit]: "Quick-Wesentlichkeitsanalyse",
    [ServiceCreditType.VsmeReport]: "VSME-Report-Generator",
    [ServiceCreditType.WorkshopKi]: "Workshop KI",
    [ServiceCreditType.WorkshopEsg]: "Workshop ESG",
};

// Mapping of service credit types to descriptions
const serviceCreditsDescriptions: Record<ServiceCreditType, string> = {
    [ServiceCreditType.QuickWesentlichkeit]: "KI-gestützte Analyse der wesentlichen ESG-Themen für Ihr Unternehmen",
    [ServiceCreditType.VsmeReport]: "Automatische Erstellung von Textbausteinen für den VSME-Nachhaltigkeitsbericht",
    [ServiceCreditType.WorkshopKi]: "90-minütiger Workshop zum Thema Künstliche Intelligenz",
    [ServiceCreditType.WorkshopEsg]: "90-minütiger Workshop zum Thema ESG und Nachhaltigkeit",
};

const ServiceCreditsDisplay: React.FC<ServiceCreditsDisplayProps> = ({ serviceCredits, isLoading }) => {
    const theme = useTheme();
    const navigate = useNavigate();

    // ⚠️ WICHTIG: useMemo muss vor allen bedingten Returns stehen (React Hooks Rules)
    // Group and process service credits
    const processedCredits = useMemo(() => {
        if (!serviceCredits || serviceCredits.length === 0) {
            return [];
        }

        // Group by type and calculate totals
        const grouped = serviceCredits.reduce((acc, credit) => {
            const type = credit.type;
            if (!acc[type]) {
                acc[type] = {
                    type,
                    totalUses: 0,
                    credits: [],
                    earliestExpiry: null as string | null,
                };
            }

            acc[type].totalUses += credit.remainingUses;
            acc[type].credits.push(credit);

            // Track earliest expiry date
            if (credit.expiresAt) {
                // ⏰ Use server time for accurate expiration comparison (respects test clock)
                if (!acc[type].earliestExpiry || new Date(credit.expiresAt) < new Date(acc[type].earliestExpiry!)) {
                    acc[type].earliestExpiry = credit.expiresAt;
                }
            }

            return acc;
        }, {} as Record<ServiceCreditType, { type: ServiceCreditType; totalUses: number; credits: ServiceCredit[]; earliestExpiry: string | null }>);

        return Object.values(grouped);
    }, [serviceCredits]);

    if (isLoading) {
        return (
            <Box sx={{ p: 2 }}>
                <Typography variant="body1">Lädt Service-Credits...</Typography>
            </Box>
        );
    }

    if (!serviceCredits || serviceCredits.length === 0) {
        return (
            <Box sx={{ p: 2 }}>
                <Typography variant="body1">Keine zusätzlichen Services gebucht. Buche weitere Services im Shop.</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 2 }}>
            <Grid2 container spacing={2}>
                {processedCredits.map((group, index) => (
                    <Grid2 size={{ xs: 12, sm: 6, md: 4 }} key={index}>
                        <Card
                            sx={{
                                height: "100%",
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "space-between",
                                p: 2,
                                pb: 1,
                                borderRadius: 2,
                                boxShadow: 2,
                                transition: "box-shadow 0.25s cubic-bezier(.4,2,.6,1), transform 0.25s cubic-bezier(.4,2,.6,1)",
                                "&:hover": {
                                    boxShadow: 6,
                                    transform: "scale(1.005)",
                                },
                            }}
                        >
                            <Box>
                                <Typography variant="h6" fontWeight="medium" gutterBottom>
                                    {serviceCreditsLabels[group.type] || String(group.type)}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    {serviceCreditsDescriptions[group.type] || ""}
                                </Typography>
                            </Box>

                            <Stack direction={"row"} justifyContent={"space-between"}>
                                <Chip label={`${group.totalUses} ${group.totalUses !== 1 ? "Nutzungen" : "Nutzung"}`} size="medium" sx={{ fontWeight: "bold" }} />

                                <IconButton onClick={() => navigate(RouteTo.AI_SERVICES)}>
                                    <icon.arrowRight />
                                </IconButton>
                            </Stack>
                        </Card>
                    </Grid2>
                ))}
            </Grid2>
        </Box>
    );
};

export default ServiceCreditsDisplay;
